/**
 * Auth Service
 *
 * Handles all authentication-related API calls including:
 * - User registration
 * - OTP sending and verification
 * - Google Sign-In
 *
 * Uses the project axios instance (apiPost from ./api) with axios-case-converter
 * for automatic camelCase <-> snake_case conversion.
 *
 * All phone numbers should be in E.164 format (+234XXXXXXXXXX)
 */

import type { AxiosError } from 'axios';
import { apiGet, apiPost } from '@/services/api';
import type { ApiResponse } from '@/types/api';
import type { TrustedDeviceDto } from '@/types/deviceTrust';

/**
 * Header sent by clients that can render the PIN-challenge response from POST /auth/login.
 * Older bundles without this header always receive OTP, preserving backward compatibility.
 */
const PIN_CHALLENGE_HEADER = { 'X-Auth-Challenge': 'pin' } as const;

/**
 * Auth tokens returned from successful verification.
 * Fields use camelCase (axios-case-converter transforms automatically).
 */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  /**
   * True when THIS authentication completed a registration (activated a
   * PENDING account) — e.g. a resumed abandoned registration via the login
   * flow, or a Google link that activated the account. Absent/null on older
   * responses and plain logins.
   */
  registeredNow?: boolean | null;
  user: {
    id: string;
    name: string;
    phone: string;
    role: string;
    status: string;
    business: {
      id: string;
      name: string;
      slug: string;
      subscriptionPlan: string;
      subscriptionStatus: string;
    } | null;
    branch: {
      id: string;
      name: string;
      isDefault: boolean;
    } | null;
  };
}

/**
 * OTP response returned from register, login, and resend endpoints.
 */
export interface OtpResponse {
  otpReference: string;
  expiresInSeconds: number;
  maskedPhone?: string;
  maskedEmail?: string;
  phone?: string;
}

/**
 * Response from POST /auth/login.
 *
 * - `challenge: 'OTP'` — OTP-bearing fields are populated; navigate to /auth/verify
 * - `challenge: 'PIN'` — `displayName` is populated; navigate to /auth/pin and skip OTP
 *
 * The PIN variant is returned only when (a) the client opted in via the
 * `X-Auth-Challenge: pin` header, (b) the `sb_device` HttpOnly cookie is present and
 * matches a server-side trusted-device row, AND (c) the user has a PIN set.
 *
 * No phone is echoed back — the server resolves the user from the cookie on the
 * subsequent POST /auth/pin/verify call.
 */
export interface LoginInitiateResponse {
  challenge: 'OTP' | 'PIN';
  otpReference: string | null;
  expiresInSeconds: number;
  maskedPhone: string | null;
  maskedEmail: string | null;
  displayName: string | null;
  /** True when the user was PENDING_VERIFICATION and this login resumes their
   * registration (REGISTRATION-type code) — label the flow as a sign-up. */
  resumedRegistration: boolean;
}

/**
 * Invitation preview returned from GET /auth/invitation-preview
 */
export interface InvitationPreview {
  name: string;
  maskedEmail: string;
  businessName: string;
  role: string;
  branchName: string | null;
}

/**
 * Google OAuth response from POST /auth/google
 */
export interface GoogleAuthResponse {
  status: 'authenticated' | 'new_user' | 'email_exists';
  tokens?: AuthTokens;
  email?: string;
  name?: string;
}

/**
 * ProblemDetail error shape from backend (RFC 7807).
 * axios-case-converter transforms snake_case fields to camelCase.
 */
interface ProblemDetailResponse {
  detail?: string;
  status?: number;
  errorCode?: string;
  title?: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Auth error codes and user-friendly messages
 */
export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  INVALID_PHONE: 'Please enter a valid Nigerian phone number',
  INVALID_EMAIL: 'Please enter a valid email address',
  OTP_EXPIRED: 'OTP has expired. Please request a new one',
  OTP_INVALID: 'Invalid verification code',
  RATE_LIMITED: 'Too many attempts. Please wait before trying again',
  USER_NOT_FOUND: 'No account found with this email address',
  USER_EXISTS: 'An account already exists with this email or phone number',
  GOOGLE_AUTH_DISABLED: 'Google sign-in is not available at this time',
  GOOGLE_TOKEN_INVALID: 'Google authentication failed. Please try again',
  NETWORK_ERROR: 'Unable to connect. Please check your internet connection',
  UNKNOWN_ERROR: 'Something went wrong. Please try again',
};

/**
 * Default error message when code is not recognized
 */
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again';

/**
 * Get user-friendly error message from API error code
 */
export function getAuthErrorMessage(code: string): string {
  return AUTH_ERROR_MESSAGES[code] ?? DEFAULT_ERROR_MESSAGE;
}

/**
 * Custom error class for auth errors
 */
export class AuthError extends Error {
  constructor(
    public code: string,
    message?: string
  ) {
    super(message ?? getAuthErrorMessage(code));
    this.name = 'AuthError';
  }
}

/**
 * Convert an AxiosError (or unknown) into an AuthError.
 *
 * Maps ProblemDetail errorCode values and HTTP status codes to AuthError codes
 * that consumers (Login.tsx, Register.tsx, VerifyOtp.tsx) already handle.
 */
function convertToAuthError(error: unknown): AuthError {
  const axiosError = error as AxiosError<ProblemDetailResponse> | undefined;

  // Network error (no response received)
  if (axiosError?.response === undefined) {
    return new AuthError('NETWORK_ERROR');
  }

  const status = axiosError.response.status;
  const data = axiosError.response.data;
  const detail = data?.detail ?? '';
  const errorCode = data?.errorCode;

  // Rate limiting
  if (status === 429) {
    return new AuthError('RATE_LIMITED');
  }

  // Map ProblemDetail errorCode to AuthError code
  if (errorCode != null) {
    if (errorCode === 'RESOURCE_NOT_FOUND') {
      return new AuthError('NOT_FOUND', detail);
    }
    if (errorCode === 'DUPLICATE_RESOURCE') {
      return new AuthError('USER_EXISTS', detail);
    }
    if (errorCode === 'VALIDATION_ERROR') {
      const lowerDetail = detail.toLowerCase();
      if (lowerDetail.includes('expired')) {
        return new AuthError('OTP_EXPIRED', detail);
      }
      if (lowerDetail.includes('invalid otp') || lowerDetail.includes('attempts exceeded')) {
        return new AuthError('OTP_INVALID', detail);
      }
      return new AuthError('VALIDATION_ERROR', detail);
    }
    // Pass through any other errorCode directly (e.g., GOOGLE_TOKEN_INVALID)
    return new AuthError(errorCode, detail);
  }

  // Fallback on HTTP status
  if (status === 404) {
    return new AuthError('NOT_FOUND', 'The requested resource was not found.');
  }
  if (status === 409) {
    return new AuthError('USER_EXISTS');
  }
  if (status >= 500) {
    return new AuthError('UNKNOWN_ERROR', 'Server error. Please try again later.');
  }

  return new AuthError('UNKNOWN_ERROR');
}

/**
 * Auth service API methods
 */
export const authService = {
  /**
   * Register a new business with owner
   *
   * Creates user + business and sends OTP for phone verification.
   *
   * @param phone - Phone number in local or E.164 format
   * @param email - Email address
   * @param businessName - Business name
   * @param ownerName - Owner's full name
   * @returns OTP reference for verification
   * @throws AuthError if phone is invalid or user already exists
   */
  async register(
    phone: string,
    email: string,
    businessName: string,
    ownerName: string,
  ): Promise<OtpResponse> {
    try {
      // axios-case-converter auto-converts camelCase -> snake_case on wire
      const response = await apiPost<ApiResponse<OtpResponse>>('/auth/register', {
        phone,
        email,
        businessName,
        ownerName,
      });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Initiate login via phone. Returns either an OTP challenge (default) or a PIN
   * challenge when the device is trusted server-side AND the user has a PIN set.
   *
   * Opts in to the PIN challenge by sending `X-Auth-Challenge: pin`. The
   * `sb_device` HttpOnly cookie is sent automatically by the browser (the axios
   * instance has `withCredentials: true`).
   */
  async initiateLoginByPhone(phone: string): Promise<LoginInitiateResponse> {
    try {
      const response = await apiPost<ApiResponse<LoginInitiateResponse>>(
        '/auth/login',
        { phone },
        { headers: { ...PIN_CHALLENGE_HEADER } },
      );
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Initiate login via email. Same trust-aware semantics as {@link initiateLoginByPhone}.
   */
  async initiateLoginByEmail(email: string): Promise<LoginInitiateResponse> {
    try {
      const response = await apiPost<ApiResponse<LoginInitiateResponse>>(
        '/auth/login',
        { email },
        { headers: { ...PIN_CHALLENGE_HEADER } },
      );
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Resolve the `sb_device` HttpOnly cookie to its trusted-device row.
   *
   * Used at app boot to recover the canonical device identity after iOS Safari ITP
   * evicts localStorage (the cookie is exempt from the 7-day script-storage wipe).
   * Returns `null` when no cookie is present, or the cookie does not match an active
   * row (the server clears a stale cookie defensively in that case).
   *
   * This endpoint is public — the cookie value itself is the credential.
   */
  async resolveMyDevice(): Promise<TrustedDeviceDto | null> {
    try {
      const response = await apiGet<ApiResponse<TrustedDeviceDto>>('/auth/devices/me');
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 404) {
        return null;
      }
      throw convertToAuthError(error);
    }
  },

  /**
   * Verify OTP and get auth tokens
   *
   * @param otpReference - OTP reference from register/login response
   * @param otpCode - 6-digit OTP code
   * @returns Auth tokens on successful verification
   * @throws AuthError if OTP is invalid or expired
   */
  async verifyOtp(otpReference: string, otpCode: string): Promise<AuthTokens> {
    try {
      const response = await apiPost<ApiResponse<AuthTokens>>('/auth/otp/verify', {
        otpReference,
        otpCode,
      });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Resend OTP code via phone
   */
  async resendOtp(phone: string): Promise<OtpResponse> {
    try {
      const response = await apiPost<ApiResponse<OtpResponse>>('/auth/otp/send', { phone });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Resend OTP code via email
   */
  async resendEmailOtp(email: string): Promise<OtpResponse> {
    try {
      const response = await apiPost<ApiResponse<OtpResponse>>('/auth/otp/send', { email });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Authenticate with Google ID token
   *
   * Flow:
   * - If user has linked Google -> returns authenticated with tokens
   * - If the Google-verified email already has an account without a Google link
   *   -> returns email_exists (route the user to email-OTP login; never links)
   * - If new user (no phone provided) -> returns new_user status
   * - If new user (phone + business provided) -> creates account + returns authenticated
   *
   * The account email is always the Google-verified one; it cannot be overridden.
   */
  async loginWithGoogle(
    credential: string,
    phone?: string,
    businessName?: string,
    ownerName?: string,
  ): Promise<GoogleAuthResponse> {
    try {
      const response = await apiPost<ApiResponse<GoogleAuthResponse>>('/auth/google', {
        credential,
        phone,
        businessName,
        ownerName,
      });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Link a Google account to an existing user and sign them in.
   *
   * The OTP pair is the inbox proof; the backend verifies the Google credential
   * BEFORE consuming the OTP, so on GOOGLE_TOKEN_INVALID the same reference+code
   * can still be submitted to plain verifyOtp as a fallback.
   */
  async linkGoogleAccount(
    otpReference: string,
    otpCode: string,
    credential: string,
  ): Promise<AuthTokens> {
    try {
      const response = await apiPost<ApiResponse<AuthTokens>>('/auth/google/link', {
        otpReference,
        otpCode,
        credential,
      });
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Fetch invitation preview (public, no auth required)
   */
  async getInvitationPreview(token: string): Promise<InvitationPreview> {
    try {
      const response = await apiGet<ApiResponse<InvitationPreview>>(
        `/auth/invitation-preview?token=${encodeURIComponent(token)}`,
      );
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Send email OTP for staff invitation acceptance (public, no auth required)
   */
  async sendInvitationOtp(token: string): Promise<OtpResponse> {
    try {
      const response = await apiPost<ApiResponse<OtpResponse>>(
        '/auth/invitation-otp',
        { token },
      );
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },

  /**
   * Accept a staff invitation and receive auth tokens (public, no auth required)
   */
  async acceptInvitation(
    token: string,
    otpReference: string,
    otpCode: string,
  ): Promise<AuthTokens> {
    try {
      const response = await apiPost<ApiResponse<AuthTokens>>(
        '/auth/accept-invitation',
        { token, otpReference, otpCode },
      );
      return response.data;
    } catch (error) {
      throw convertToAuthError(error);
    }
  },
};
